import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';

// Różnica zegar serwera - zegar przeglądarki, liczona z `iat` świeżego tokena.
// Bez tego spóźniony zegar u usera sprawia, że wygasły token wygląda na ważny
// i panel nigdy nie woła /auth/refresh/ (incydent 2026-10-20).
const computeClockSkewMs = (access: string): number => {
  try {
    const { iat } = jwtDecode<{ iat?: number }>(access);
    return iat ? iat * 1000 - Date.now() : 0;
  } catch {
    return 0;
  }
};

interface UserGroup {
  id: number;
  name: string;
}

export interface User {
  id: number;
  email: string;
  full_name: string;
  groups: UserGroup[];
  user_permissions: string[];
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  clockSkewMs: number;
  isAuthenticated: boolean;

  setTokens: (access: string, refresh: string) => void;
  setUser: (user: User) => void;
  logout: () => void;
  isAdmin: () => boolean;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      clockSkewMs: 0,
      isAuthenticated: false,

      setTokens: (access: string, refresh: string) =>
        set({ accessToken: access, refreshToken: refresh, clockSkewMs: computeClockSkewMs(access) }),

      setUser: (user: User) =>
        set({ user, isAuthenticated: true }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          clockSkewMs: 0,
          isAuthenticated: false,
        }),

      isAdmin: () => {
        const { user } = get();
        return user?.groups?.some((g) => g.name === 'admin') ?? false;
      },

      hasPermission: (permission: string) => {
        const { user } = get();
        return user?.user_permissions?.includes(permission) ?? false;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        clockSkewMs: state.clockSkewMs,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Helper functions to access tokens outside of React components
export const getAccessToken = () => useAuthStore.getState().accessToken;
export const getRefreshToken = () => useAuthStore.getState().refreshToken;
// `?? 0` - stan zapisany przed wprowadzeniem clockSkewMs nie ma tego pola
export const getClockSkewMs = () => useAuthStore.getState().clockSkewMs ?? 0;
export const setTokens = (access: string, refresh: string) =>
  useAuthStore.getState().setTokens(access, refresh);
export const logout = () => useAuthStore.getState().logout();
